import { useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type NavigateOptions, useNavigate } from "@tanstack/react-router";
import { useAppForm } from "~/components/ui/form";
import { parseValidationErrors } from "~/lib/errors";
import { sessionQueryOptions } from "~/modules/auth/functions/session";
import { useTRPC } from "~/trpc/client";
import { type AuthLoginInput, authLoginSchema } from "./login.schema";

export function useAuthLoginForm({
	defaultValues,
	redirect,
}: {
	defaultValues?: Partial<AuthLoginInput>;
	redirect?: NavigateOptions;
} = {}) {
	const { t } = useLingui();
	const queryClient = useQueryClient();
	const trpc = useTRPC();
	const navigate = useNavigate();

	const authLoginMutation = useMutation(
		trpc.auth.login.mutationOptions({
			async onSuccess() {
				await queryClient.invalidateQueries();
				await queryClient.fetchQuery(sessionQueryOptions());
				await navigate(
					redirect
						? redirect
						: { from: "/auth/login", to: "/manager", replace: true },
				);
			},
		}),
	);

	return useAppForm({
		defaultValues: {
			email: "",
			password: "",
			...defaultValues,
		},
		canSubmitWhenInvalid: true,
		validators: {
			onSubmit: authLoginSchema,
			onSubmitAsync: async ({ value }) => {
				try {
					await authLoginMutation.mutateAsync(value);
				} catch (e) {
					return parseValidationErrors(e, t`Something went wrong.`);
				}
			},
		},
	});
}
